/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
const colors = {
    transparent: 'transparent',
    current: 'currentColor',
    black: '#000000',
    warmBlack: '#25252B',
    white: '#FFFFFF',
    primaryBlack: '#25252B',
    charcoal: '#373637',
    warmWhite: '#F0EFEB',
    // Brand-specific colors below are defaults/fallbacks.
    // Actual brand values are defined in theme/brands/<brand>/colors.js
    // and deep-merged via extendTheme.
    loyaltyT1: '#F59ED1',
    loyaltyT2: '#F361B8',
    loyaltyT3: '#AC0967',
    loyaltyPink: '#FDE6F4',
    brandMagenta: '#EC008C',
    brandMagentaText: '#D9017A',
    successText: '#048848',
    successBG: '#F9F1E5',
    alertSuccessBG: '#EBF5F3',
    warningText: '#875900',
    warningBg: '#F9F1E5',
    dangerText: '#B30000',
    dangerBG: '#F9BFBF',
    hyperlink: '#005FCC',
    sale: '#CF0000',
    cardDivider: '#D3D3D3',
    darkPink: '#91155C',
    alternativeWarning: {
        100: '#FFF7E6',
        200: '#F2F2F280'
    },
    storeReceiptBorderColor: '#C8C8CA',

    webBasic: {
        50: '#F9F9FB',
        100: '#F1F1F1',
        150: '#F0EFEB',
        200: '#ECE9E9',
        250: '#E2DFDA',
        300: '#DBDBDB',
        400: '#C5C5C5',
        500: '#AFAFAF',
        600: '#939393',
        700: '#676767',
        800: '#585858',
        900: '#373637',
        950: '#25252B'
    },

    webTheme: {
        focus: '#EC008C',
        layerDefault: '#FFFFFF',
        layerActive: 'rgba(0, 0, 0, 0.3)',
        backgroundDefault: '#FFFFFF',
        backgroundPrimaryBrand: '#F0EFEB',
        backgroundSecondaryBrand: '#E2DFDA',
        backgroundReverse: '#000000',
        backgroundLoyaltyCreator: '#F59ED1',
        backgroundLoyaltyCreatorSecondary: '#FDE9F5',
        backgroundLoyaltyMuse: '#F361B8',
        backgroundLoyaltyMuseSecondary: '#FDDAEF',
        backgroundLoyaltyIcon: '#AC0967',
        backgroundLoyaltyIconSecondary: '#EBC7DD',
        backgroundLoyaltyBanner: '#EC008C',
        backgroundNotification: '#F1F1F1',
        fieldDefault: '#FFFFFF',
        fieldHover: '#F1F1F1',
        fieldHoverSelected: '#DBDBDB',
        fieldDisabled: '#F1F1F1',
        supportSuccess: '#048848',
        supportError: '#B30000',
        supportWarning: '#FFBB33',
        supportInfo: '#000000',
        strokeDefault: '#000000',
        strokeSecondary: '#767676',
        strokeDisable: '#AFAFAF',
        strokeTertiary: '#ECE9E9',
        strokeWarning: '#B30000',
        strokeReverse: '#FFFFFF',
        strokeInteractive: '#EC008C',
        iconPrimary: '#000000',
        iconOnColor: '#FFFFFF',
        iconDisabled: '#AFAFAF',
        iconLoyaltyCreator: '#F59ED1',
        iconLoyaltyMuse: '#F361B8',
        iconLoyaltyIcon: '#AC0967',
        tagDefault: '#FFFFFF',
        tagOnImage: 'rgba(255, 255, 255, 0.7)',
        tagReverse: '#000000',
        tagBrand: '#D9017A',
        redTint: '#FFEBEB'
    },

    webTypo: {
        textPrimary: '#000000',
        textSecondary: '#585858',
        textTertiary: '#676767',
        textStrikethrough: '#676767',
        textOnColor: '#FFFFFF',
        textOnColorDisabled: '#DBDBDB',
        textDisabled: '#939393',
        textDisabledReverse: '#DBDBDB',
        textPrimaryReverse: '#FFFFFF',
        textError: '#B30000',
        textSale: '#CF0000',
        textSaleInverse: '#FF2D2D',
        textBrandLoyalty: '#D9017A',
        linkPrimary: '#000000'
    },

    webButton: {
        btnPrimaryDefault: '#000000',
        btnPrimaryHover: '#373637',
        btnPrimaryDisabled: '#C5C5C5',
        btnPrimaryReverseDefault: '#FFFFFF',
        btnPrimaryReverseHover: '#DBDBDB',
        btnPrimaryReverseDisabled: '#C5C5C5',
        btnSecondaryDefault: '#FFFFFF',
        btnSecondaryHover: '#000000',
        btnSecondaryDisabled: '#FFFFFF',
        btnTertiaryTextDefault: '#000000',
        btnTertiaryTextHover: '#585858',
        btnTertiaryTextDisabled: '#AFAFAF'
    },

    webTrasparency: {
        neutralTransparencyBlack: 'rgba(0, 0, 0, 0.30)'
    },

    whiteAlpha: {
        50: 'rgba(255, 255, 255, 0.04)',
        100: 'rgba(255, 255, 255, 0.06)',
        200: 'rgba(255, 255, 255, 0.08)',
        300: 'rgba(255, 255, 255, 0.16)',
        400: 'rgba(255, 255, 255, 0.24)',
        500: 'rgba(255, 255, 255, 0.36)',
        600: 'rgba(255, 255, 255, 0.48)',
        700: 'rgba(255, 255, 255, 0.64)',
        750: 'rgba(255, 255, 255, 0.70)',
        800: 'rgba(255, 255, 255, 0.80)',
        900: 'rgba(255, 255, 255, 0.92)'
    },

    blackAlpha: {
        50: 'rgba(0, 0, 0, 0.04)',
        100: 'rgba(0, 0, 0, 0.06)',
        200: 'rgba(0, 0, 0, 0.08)',
        300: 'rgba(0, 0, 0, 0.16)',
        400: 'rgba(0, 0, 0, 0.24)',
        500: 'rgba(0, 0, 0, 0.36)',
        600: 'rgba(0, 0, 0, 0.48)',
        700: 'rgba(0, 0, 0, 0.64)',
        800: 'rgba(0, 0, 0, 0.80)',
        900: 'rgba(0, 0, 0, 0.92)'
    },

    gray: {
        50: '#ECE9E9',
        100: '#CFCFCF',
        200: '#F0EFEB',
        300: '#AEAEAE',
        400: '#A0A0A0',
        450: '#ECE9E9',
        500: '#939393',
        600: '#747474',
        700: '#676767',
        800: '#B2B2B2',
        900: '#181818',
    },

    grey: {
        200: '#F9F9FB',
        300: '#F1F1F1',
        400: '#ECE9E9',
        500: '#CBCED2',
        600: '#939393',
        700: '#676767',
        800: '#585858'
    },

    red: {
        50: '#FEF1EE',
        100: '#FEDED8',
        200: '#FEB8AB',
        300: '#FE8F7D',
        400: '#FE7765',
        500: '#FE5C4C',
        600: '#EA001E',
        700: '#CF0000',
        800: '#8E030F',
        900: '#640103'
    },

    orange: {
        50: '#FEF1ED',
        100: '#FFDED5',
        200: '#FEB9A5',
        300: '#FF906E',
        400: '#FF784F',
        500: '#FF5D2D',
        600: '#D83A00',
        700: '#AA3001',
        800: '#7E2600',
        900: '#541D01'
    },

    yellow: {
        50: '#FBF3E0',
        100: '#F9E3B6',
        200: '#FCC003',
        300: '#E4A201',
        400: '#D79304',
        500: '#CA8501',
        600: '#A86403',
        700: '#8C4B02',
        800: '#6F3400',
        900: '#4F2100'
    },

    green: {
        50: '#EBF7E6',
        100: '#CDEFC4',
        200: '#91DB8B',
        300: '#45C65A',
        400: '#41B658',
        500: '#3BA755',
        600: '#2E844A',
        700: '#22683E',
        800: '#194E31',
        900: '#0E3522'
    },

    teal: {
        50: '#DEF9F3',
        100: '#ACF3E4',
        200: '#04E1CB',
        300: '#01C3B3',
        400: '#03B4A7',
        500: '#06A59A',
        600: '#0B827C',
        700: '#056764',
        800: '#024D4C',
        900: '#023434'
    },

    blue: {
        50: '#EEF4FF',
        100: '#D8E6FE',
        200: '#AACBFF',
        300: '#78B0FD',
        400: '#57A3FD',
        500: '#1B96FF',
        600: '#0176D3',
        700: '#0B5CAB',
        800: '#014486',
        900: '#032D60'
    },

    cyan: {
        50: '#EAF5FE%',
        100: '#CFE9FE',
        200: '#90D0FE',
        300: '#1AB9FF',
        400: '#08ABED',
        500: '#0D9DDA',
        600: '#107CAD',
        700: '#05628A',
        800: '#084968',
        900: '#023248'
    },

    purple: {
        50: '#F6F2FB',
        100: '#ECE1F9',
        200: '#D78FF5',
        300: '#C29EF1',
        400: '#B78DEF',
        500: '#AD7BEE',
        600: '#9050E9',
        700: '#7526E3',
        800: '#5A1BA9',
        900: '#401075'
    },

    pink: {
        50: '#F9F0FF',
        100: '#F2DEFE',
        200: '#E5B9FE',
        300: '#D892FE',
        400: '#D17DFE',
        500: '#CB65FF',
        600: '#BA01FF',
        700: '#9602C7',
        800: '#730394',
        900: '#EC008C'
    },

    progressPink: {
        500: '#D9017A'
    }
}

colors.brand = {
    100: colors.gray[100],
    500: colors.pink[900]
}

export default colors
